import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook to implement additional security measures and monitoring
 */
export const useSecurityHeaders = () => {
  const { user } = useAuth();

  useEffect(() => {
    // Enhanced production security measures
    const handleContextMenu = (e: MouseEvent) => {
      if (import.meta.env.PROD) {
        e.preventDefault();
        console.warn('Security: Context menu access attempted');
      }
    };

    // Enhanced developer tools blocking
    const handleKeyDown = (e: KeyboardEvent) => {
      if (import.meta.env.PROD) {
        // Disable F12, Ctrl+Shift+I/J/K, Ctrl+U, Ctrl+Shift+C
        if (e.key === 'F12' || 
            (e.ctrlKey && e.shiftKey && ['I', 'J', 'K', 'C'].includes(e.key.toUpperCase())) ||
            (e.ctrlKey && e.key.toLowerCase() === 'u')) {
          e.preventDefault();
          console.warn('Security: Developer tools access attempt blocked');
        }
      }
    };

    // Enhanced security monitoring
    let rapidClickCount = 0;
    let rapidKeyCount = 0;
    let rapidClickTimer: NodeJS.Timeout;
    let rapidKeyTimer: NodeJS.Timeout;

    const handleClick = () => {
      rapidClickCount++;
      clearTimeout(rapidClickTimer);
      rapidClickTimer = setTimeout(() => {
        if (rapidClickCount > 15) {
          console.warn('Security: Suspicious rapid clicking detected -', rapidClickCount, 'clicks');
        }
        rapidClickCount = 0;
      }, 1000);
    };

    const handleKeyPress = () => {
      rapidKeyCount++;
      clearTimeout(rapidKeyTimer);
      rapidKeyTimer = setTimeout(() => {
        if (rapidKeyCount > 50) {
          console.warn('Security: Suspicious rapid key pressing detected');
        }
        rapidKeyCount = 0;
      }, 1000);
    };

    // Enhanced data cleanup on page unload
    const handleBeforeUnload = () => {
      try {
        // Clear sensitive temporary data
        sessionStorage.removeItem('temp_admin_data');
        sessionStorage.removeItem('temp_user_data');
        
        // Clear cache that might contain sensitive info
        Object.keys(localStorage).forEach(key => {
          if (key.includes('temp_') || 
              key.includes('cache_sensitive') || 
              key.includes('admin_session')) {
            localStorage.removeItem(key);
          }
        });
      } catch (error) {
        console.warn('Security cleanup error:', error);
      }
    };

    // Add event listeners with enhanced monitoring
    if (import.meta.env.PROD) {
      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('keydown', handleKeyDown);
    }
    
    document.addEventListener('click', handleClick);
    document.addEventListener('keypress', handleKeyPress);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keypress', handleKeyPress);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearTimeout(rapidClickTimer);
      clearTimeout(rapidKeyTimer);
    };
  }, [user]);

  // Enhanced admin security monitoring
  useEffect(() => {
    if (!user) return;

    let adminSessionStart = Date.now();
    
    // Enhanced admin area monitoring
    const handleVisibilityChange = () => {
      if (window.location.pathname.includes('/admin')) {
        if (document.hidden) {
          const sessionDuration = Date.now() - adminSessionStart;
          console.warn('Security: Admin session visibility change -', 
            'Duration:', Math.round(sessionDuration / 1000), 'seconds');
        } else {
          adminSessionStart = Date.now(); // Reset session timer
        }
      }
    };

    const handleFocus = () => {
      if (window.location.pathname.includes('/admin')) {
        console.info('Security: Admin area gained focus');
      }
    };

    const handleBlur = () => {
      if (window.location.pathname.includes('/admin')) {
        console.warn('Security: Admin area lost focus - potential security risk');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [user]);
};